import { ExternalLink } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import clutchReviewEliteEdge from '@/assets/clutch-review-elite-edge.png';
import clutchReviewRecruitment from '@/assets/clutch-review-recruitment.png';
import goodFirmsReview1 from '@/assets/goodfirms-review-1.webp';
import goodFirmsReview2 from '@/assets/goodfirms-review-2.webp';
import goodFirmsReview3 from '@/assets/goodfirms-review-3.webp';
import goodFirmsReview4 from '@/assets/goodfirms-review-4.webp';
import { GoodFirmsBadge } from '@/components/badges/GoodFirmsBadge';

const clutchReviews = [
  {
    image: clutchReviewEliteEdge,
    title: 'Mobile App & Web Development for Event Management Company',
    link: 'https://clutch.co/go-to-review/05f3e8c2-809c-44b4-acad-cbae67b292ae/345033',
  },
  {
    image: clutchReviewRecruitment,
    title: 'Application Development for Recruitment Firm',
    link: 'https://clutch.co/go-to-review/05f3e8c2-809c-44b4-acad-cbae67b292ae/344691',
  },
];

const goodFirmsReviews = [
  {
    image: goodFirmsReview1,
    title: 'GoodFirms Review 1',
    link: 'https://www.goodfirms.co/company/ortem-technologies-llc',
  },
  {
    image: goodFirmsReview2,
    title: 'GoodFirms Review 2',
    link: 'https://www.goodfirms.co/company/ortem-technologies-llc',
  },
  {
    image: goodFirmsReview3,
    title: 'GoodFirms Review 3',
    link: 'https://www.goodfirms.co/company/ortem-technologies-llc',
  },
  {
    image: goodFirmsReview4,
    title: 'GoodFirms Review 4',
    link: 'https://www.goodfirms.co/company/ortem-technologies-llc',
  },
];

export function TrustBadges() {
  return (
    <section className="py-16 border-y border-border bg-card/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">
            Independently Verified
          </p>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground">
            Highly Rated & Verified on <span className="gradient-text">Clutch, GoodFirms & More</span>
          </h2>
          <p className="mt-3 text-muted-foreground text-base max-w-xl mx-auto">
            Real reviews from real clients — verified by independent platforms. No edits, no filters.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6">
            {(['mobile-us', 'web-de', 'software-de', 'ai-nlp-us'] as const).map((id) => (
              <GoodFirmsBadge key={id} award={id} size={150} />
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row justify-center gap-12 items-center">
          {/* GoodFirms Reviews Carousel */}
          <div className="flex flex-col items-center w-full max-w-xl">
            <h3 className="text-lg font-semibold text-foreground mb-6">GoodFirms Reviews</h3>
            <Carousel
              opts={{
                align: 'start',
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 5000,
                }),
              ]}
              className="w-full max-w-xl"
            >
              <CarouselContent>
                {goodFirmsReviews.map((review, index) => (
                  <CarouselItem key={index}>
                    <a
                      href={review.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <div className="rounded-xl overflow-hidden border border-border bg-background shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col">
                        <div className="p-6 bg-card flex items-center justify-center h-[223px] rounded-t-xl">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            width="400"
                            height="200"
                          />
                        </div>
                        <div className="p-4 flex items-center justify-between bg-card border-t border-border mt-auto hover:bg-accent/5 transition-colors rounded-b-xl">
                          <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                            Read Full Review on GoodFirms
                          </span>
                          <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex -left-4" />
              <CarouselNext className="hidden sm:flex -right-4" />
            </Carousel>
          </div>

          {/* Clutch Reviews Carousel */}
          <div className="flex flex-col items-center w-full max-w-xl">
            <h3 className="text-lg font-semibold text-foreground mb-6">Clutch Reviews</h3>
            <Carousel
              opts={{
                align: 'start',
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 5000,
                }),
              ]}
              className="w-full max-w-xl"
            >
              <CarouselContent>
                {clutchReviews.map((review, index) => (
                  <CarouselItem key={index}>
                    <a
                      href={review.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <div className="rounded-xl overflow-hidden border border-border bg-background shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col">
                        <div className="p-6 bg-card flex items-center justify-center h-[223px] rounded-t-xl">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            width="400"
                            height="200"
                          />
                        </div>
                        <div className="p-4 flex items-center justify-between bg-card border-t border-border mt-auto hover:bg-accent/5 transition-colors rounded-b-xl">
                          <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                            Read Full Review on Clutch
                          </span>
                          <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex -left-4" />
              <CarouselNext className="hidden sm:flex -right-4" />
            </Carousel>
          </div>
        </div>
      </div>
    </section>
  );
}
